import { useRef, useCallback } from 'react';

/**
 * Attaches scroll-wheel and middle-mouse-button drag scrubbing to a DOM
 * element. Mirrors the interaction pattern of SpinInputNumber but works with
 * any element type (div, input, etc.) via a callback ref.
 *
 * Usage:
 *   const scrub = useScrollDrag({ value, min, max, step, onChange });
 *   <div ref={scrub.ref} onPointerDown={scrub.onPointerDown}
 *        onPointerMove={scrub.onPointerMove} onPointerUp={scrub.onPointerUp} />
 */
interface Options {
  value:    number;
  min?:     number;
  max?:     number;
  step?:    number;
  onChange: (val: number) => void;
}

function clampStep(v: number, min: number | undefined, max: number | undefined, step: number): number {
  if (typeof min === 'number') v = Math.max(min, v);
  if (typeof max === 'number') v = Math.min(max, v);
  const decimals = String(step).includes('.') ? String(step).split('.')[1].length : 0;
  return parseFloat(v.toFixed(decimals));
}

export function useScrollDrag<T extends HTMLElement>(opts: Options) {
  const optsRef = useRef(opts);
  optsRef.current = opts;

  // Stable wheel handler — reads live opts via ref, never stale.
  const onWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
    const { value: v, min, max, step = 1, onChange } = optsRef.current;
    onChange(clampStep(v + (e.deltaY < 0 ? 1 : -1) * step, min, max, step));
  }, []);

  // Callback ref — fires on mount/unmount so wheel listener tracks the element
  // even when it is conditionally rendered (e.g. toggling between display/edit).
  const elementRef = useRef<T | null>(null);
  const ref = useCallback((el: T | null) => {
    if (elementRef.current) elementRef.current.removeEventListener('wheel', onWheel);
    elementRef.current = el;
    if (el) el.addEventListener('wheel', onWheel, { passive: false });
  }, [onWheel]);

  // Middle-mouse drag
  const dragRef = useRef<{ startY: number; startValue: number } | null>(null);

  const onPointerDown = useCallback((e: React.PointerEvent<T>) => {
    if (e.button !== 1) return;
    e.preventDefault();
    dragRef.current = { startY: e.clientY, startValue: optsRef.current.value };
    (e.currentTarget as T).setPointerCapture(e.pointerId);
    document.body.style.cursor = 'ns-resize';
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent<T>) => {
    if (!dragRef.current) return;
    const { min, max, step = 1, onChange } = optsRef.current;
    const rawDelta = (dragRef.current.startY - e.clientY) / 4;
    onChange(clampStep(dragRef.current.startValue + rawDelta * step, min, max, step));
  }, []);

  const onPointerUp = useCallback((e: React.PointerEvent<T>) => {
    if (e.button !== 1) return;
    dragRef.current = null;
    document.body.style.cursor = '';
  }, []);

  return { ref, onPointerDown, onPointerMove, onPointerUp };
}
